import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { Loader2 } from "lucide-react";

import useComponentFocus from "@/internals/hooks/useComponentFocus";
import { cn } from "@/lib/utils";
import { BaseButtonProps } from "./types";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default:
          "flex gap-[10px] justify-center bg-primary-green text-destructive-foreground hover:cursor-pointer ",
        destructive:
          "bg-destructive-light text-destructive hover:bg-[#FFE2E2] hover:text-destructive hover:cursor-pointer",
        outline:
          "border border-primary-green text-destructive-foreground hover:cursor-pointer ",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        secondary:
          "bg-primary-green-pale text-primary-green text-[1.125rem] font-[700] p-[0.5rem]",
      },
      size: {
        default: "h-10 px-[20px] py-[22px] w-full ",
        sm: "h-14 rounded-md p-[22px] w-1/2",
        md: "h-14 rounded-md py-6 px-5 w-full",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    BaseButtonProps,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      leftIcon: LeftIcon,
      rightIcon: RightIcon,
      children,
      disabled: _disabled,
      loading,
      ...props
    },
    ref
  ) => {
    const disabled = _disabled || loading;
    const Comp = asChild ? Slot : "button";

    const { focused, ...rest } = useComponentFocus();

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        disabled={disabled}
        ref={ref}
        {...rest}
        {...props}
      >
        {LeftIcon && (
          <span>
            {typeof LeftIcon === "function" ? LeftIcon({ focused }) : LeftIcon}
          </span>
        )}
        {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
        {children}
        {RightIcon && (
          <span>
            {typeof RightIcon === "function"
              ? RightIcon({ focused })
              : RightIcon}
          </span>
        )}
      </Comp>
    );
  }
);
Button.displayName = "Button";

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants };
